import { formatDurationHours, formatDurationMinutes } from 'src/app/utils/format-duration'
import { StatusTone, VarianceLevel } from '../models/detail-page.model'

/**
 * Re-exports of the canonical duration formatters so anything importing
 * from `@shared/detail-page` stays on a single source of truth — see
 * `src/app/utils/format-duration.ts` for the unit policy (m / h / d).
 */
export const formatMinutes = formatDurationMinutes
export const formatHours = formatDurationHours

export function variancePctFromRatio(ratio: number | null | undefined): number | null {
    if (ratio == null || isNaN(ratio)) return null
    return Math.round((ratio - 1) * 100)
}

/**
 * Convert a variance ratio (`actual / standard`) into an efficiency percentage.
 * 100% = on target, >100% = ahead of target, <100% = behind target.
 * ratio = 2.0 → 50%, ratio = 1.0 → 100%, ratio = 0.5 → 200%.
 */
export function efficiencyPctFromRatio(ratio: number | null | undefined): number | null {
    if (ratio == null || isNaN(ratio) || ratio <= 0) return null
    return Math.round((1 / ratio) * 100)
}

export function varianceLevelFromRatio(ratio: number | null | undefined): VarianceLevel {
    if (ratio == null || isNaN(ratio)) return 'unknown'
    if (ratio > 1.1) return 'over'
    if (ratio < 0.9) return 'under'
    return 'ok'
}

export function varianceClassFromRatio(ratio: number | null | undefined): string {
    return `variance-${varianceLevelFromRatio(ratio)}`
}

/**
 * Efficiency-framed level. Thresholds (per #3111):
 *   ≥ 90% efficiency (ratio ≤ 1.111) → ok (green)
 *   70-89% efficiency (ratio 1.111-1.428) → under (amber)
 *   < 70% efficiency (ratio > 1.428) → over (red)
 *
 * Reuses the existing VarianceLevel taxonomy so existing CSS keeps working;
 * only the threshold semantics flip from "more variance" to "lower efficiency".
 */
export function efficiencyLevelFromRatio(ratio: number | null | undefined): VarianceLevel {
    const eff = efficiencyPctFromRatio(ratio)
    if (eff == null) return 'unknown'
    if (eff < 70) return 'over'
    if (eff < 90) return 'under'
    return 'ok'
}

export function efficiencyClassFromRatio(ratio: number | null | undefined): string {
    return `variance-${efficiencyLevelFromRatio(ratio)}`
}

export function statusTone(status: string | undefined | null): StatusTone {
    switch ((status || '').toLowerCase()) {
        case 'running':
        case 'active':
        case 'in_progress':
            return 'primary'
        case 'completed':
        case 'success':
        case 'ok':
            return 'success'
        case 'aborted':
        case 'cancelled':
            return 'secondary'
        case 'warning':
        case 'pending':
            return 'warning'
        case 'error':
        case 'failed':
        case 'danger':
            return 'danger'
        case 'info':
            return 'info'
        default:
            return 'neutral'
    }
}
